"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useMemo, useEffect } from "react";
import { Input, Button } from "react-aria-components";
import { sendGTMEvent } from "@next/third-parties/google";
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  placeholder?: string;
  className?: string;
  isSearching?: boolean;
  "aria-label"?: string;
}

/**
 * Optimized SearchInput component - simplified and focused
 * Removed suggestions temporarily for stability, can be re-added later with proper implementation
 */
export function SearchInput(t0) {
  const $ = _c(35);
  const {
    value,
    onChange,
    onSearch,
    placeholder: t1,
    className: t2,
    isSearching: t3,
    "aria-label": t4
  } = t0;
  const placeholder = t1 === undefined ? "Enter Make, Model, or Keyword" : t1;
  const className = t2 === undefined ? "" : t2;
  const isSearching = t3 === undefined ? false : t3;
  const ariaLabel = t4 === undefined ? "Search vehicle inventory" : t4;
  const [localValue, setLocalValue] = useState(value);
  let t5;
  if ($[0] !== onChange) {
    t5 = e => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      onChange(newValue);
    };
    $[0] = onChange;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const handleInputChange = t5;
  let t6;
  if ($[2] !== localValue || $[3] !== onSearch) {
    t6 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        sendGTMEvent({
          event: "search",
          search_term: localValue,
          search_type: "vehicle_inventory",
          search_method: "enter_key",
          has_search_term: !!localValue.trim()
        });
        onSearch();
      }
    };
    $[2] = localValue;
    $[3] = onSearch;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleKeyDown = t6;
  let t7;
  if ($[5] !== localValue || $[6] !== onChange) {
    t7 = () => {
      sendGTMEvent({
        event: "search_clear",
        had_content: !!localValue.trim(),
        content_length: localValue.length
      });
      setLocalValue("");
      onChange("");
    };
    $[5] = localValue;
    $[6] = onChange;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleClear = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg className="t-w-5 t-h-5 t-text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const searchIcon = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <svg className="t-w-5 t-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const clearIcon = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <svg className="t-w-4 t-h-4 t-text-brand-violet t-animate-spin" fill="none" viewBox="0 0 24 24"><circle className="t-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="t-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const loadingSpinner = t10;
  let t11;
  if ($[11] !== localValue || $[12] !== value) {
    t11 = () => {
      if (value !== localValue) {
        setLocalValue(value);
      }
    };
    $[11] = localValue;
    $[12] = value;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== value) {
    t12 = [value];
    $[14] = value;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  useEffect(t11, t12);
  const t13 = `t-relative ${className}`;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="t-absolute t-inset-y-0 t-left-0 t-pl-4 t-flex t-items-center t-pointer-events-none">{searchIcon}</div>;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== ariaLabel || $[18] !== handleInputChange || $[19] !== handleKeyDown || $[20] !== isSearching || $[21] !== localValue || $[22] !== placeholder) {
    t15 = <Input className="t-min-h-12 t-w-full t-rounded-none t-border t-border-slate-300 t-bg-white t-py-3 t-pl-12 t-pr-12 t-text-base t-text-automotive-dark t-shadow-sm t-transition-all placeholder:t-text-gray-500 focus:t-border-brand-violet focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet" placeholder={placeholder} value={localValue} onChange={handleInputChange} onKeyDown={handleKeyDown} autoComplete="off" disabled={isSearching} aria-label={ariaLabel} />;
    $[17] = ariaLabel;
    $[18] = handleInputChange;
    $[19] = handleKeyDown;
    $[20] = isSearching;
    $[21] = localValue;
    $[22] = placeholder;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isSearching) {
    t16 = isSearching && <div className="t-absolute t-inset-y-0 t-right-12 t-flex t-items-center t-pointer-events-none">{loadingSpinner}</div>;
    $[24] = isSearching;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== handleClear || $[27] !== isSearching || $[28] !== localValue) {
    t17 = localValue && !isSearching && <Button className="t-absolute t-inset-y-0 t-right-0 t-flex t-w-12 t-items-center t-justify-center t-border-none t-bg-transparent t-text-gray-400 t-transition-colors hover:t-text-gray-700 t-cursor-pointer" onPress={handleClear} aria-label="Clear search">{clearIcon}</Button>;
    $[26] = handleClear;
    $[27] = isSearching;
    $[28] = localValue;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t13 || $[31] !== t15 || $[32] !== t16 || $[33] !== t17) {
    t18 = <div className={t13}>{t14}{t15}{t16}{t17}</div>;
    $[30] = t13;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
}